import React, { useState, useEffect } from 'react';
import { Send, Calendar, FileText, History, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsletterManagement = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('create'); // 'create' or 'history'
  const [newsletters, setNewsletters] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    from_date: '',
    to_date: '',
    custom_content: ''
  });
  
  const [generatedData, setGeneratedData] = useState(null);

  useEffect(() => {
    if (view === 'history') {
      fetchNewsletters();
    }
  }, [view]);

  const fetchNewsletters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/newsletters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewsletters(response.data);
    } catch (error) {
      toast.error('Failed to fetch newsletters');
    }
  };

  const handleGenerateData = async () => {
    if (!formData.from_date || !formData.to_date) {
      toast.error('Please select date range');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/newsletters/generate?from_date=${formData.from_date}&to_date=${formData.to_date}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGeneratedData(response.data);
      toast.success('Newsletter data generated successfully!');
    } catch (error) {
      toast.error('Failed to generate newsletter data');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = () => {
    if (!generatedData) {
      toast.error('Please generate data first');
      return;
    }
    setPreviewData({ ...formData, generated_data: generatedData });
    setShowPreview(true);
  };

  const handleSendNewsletter = async () => {
    if (!formData.title || !formData.from_date || !formData.to_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!generatedData) {
      toast.error('Please generate data first');
      return;
    }

    if (!window.confirm('Are you sure you want to send this newsletter to all team members?')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/newsletters`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Newsletter sent successfully to all team members!');
      setFormData({ title: '', from_date: '', to_date: '', custom_content: '' });
      setGeneratedData(null);
      setView('history');
    } catch (error) {
      toast.error('Failed to send newsletter');
    } finally {
      setLoading(false);
    }
  };

  const getMoodEmoji = (mood) => {
    const map = { 'Happy': '😊', 'Inspired': '✨', 'Overworked': '😰', 'Bored': '😑', 'Low': '😔' };
    return map[mood] || '😐';
  };

  return (
    <div className="management-page">
      <div className="page-header">
        <h2>Newsletter Management</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            className={view === 'create' ? 'gradient-btn' : 'btn-outline'}
            onClick={() => setView('create')}
          >
            <FileText size={18} /> Create Newsletter
          </button>
          <button 
            className={view === 'history' ? 'gradient-btn' : 'btn-outline'}
            onClick={() => setView('history')}
          >
            <History size={18} /> Newsletter History
          </button>
        </div>
      </div>

      {view === 'create' ? (
        <div className="newsletter-create">
          {/* Newsletter Form */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '20px', color: '#7077FE' }}>Newsletter Details</h3>
            
            <div style={{ display: 'grid', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Newsletter Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Weekly Team Update - Nov 2024"
                  style={{ 
                    width: '100%', 
                    padding: '12px', 
                    border: '1px solid #ddd', 
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    <Calendar size={16} style={{ display: 'inline', marginRight: '5px' }} />
                    From Date *
                  </label>
                  <input
                    type="date"
                    value={formData.from_date}
                    onChange={(e) => setFormData({ ...formData, from_date: e.target.value })}
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      border: '1px solid #ddd', 
                      borderRadius: '8px',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    <Calendar size={16} style={{ display: 'inline', marginRight: '5px' }} />
                    To Date *
                  </label>
                  <input
                    type="date"
                    value={formData.to_date}
                    onChange={(e) => setFormData({ ...formData, to_date: e.target.value })}
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      border: '1px solid #ddd', 
                      borderRadius: '8px',
                      fontSize: '16px'
                    }}
                  />
                </div>
              </div>

              <div>
                <button 
                  onClick={handleGenerateData}
                  disabled={loading || !formData.from_date || !formData.to_date}
                  className="gradient-btn"
                  style={{ width: '100%' }}
                >
                  {loading ? 'Generating...' : 'Generate Newsletter Data'}
                </button>
              </div>
            </div>
          </div>

          {/* Generated Data Preview */}
          {generatedData && (
            <div className="card" style={{ marginBottom: '20px' }}>
              <h3 style={{ marginBottom: '20px', color: '#7077FE' }}>Newsletter Metrics Preview</h3>
              
              {/* Overview Metrics */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginBottom: '30px' }}>
                <div style={{ 
                  background: 'linear-gradient(135deg, #CE8FFB 0%, #7077FE 100%)', 
                  padding: '24px', 
                  borderRadius: '12px',
                  color: 'white'
                }}>
                  <h2 style={{ margin: '0 0 8px 0', fontSize: '42px', fontWeight: 'bold' }}>
                    {generatedData.total_tasks_completed}
                  </h2>
                  <p style={{ margin: 0, fontSize: '16px', opacity: 0.9 }}>Tasks Completed</p>
                </div>
                <div style={{ 
                  background: 'linear-gradient(135deg, #7077FE 0%, #CE8FFB 100%)', 
                  padding: '24px', 
                  borderRadius: '12px',
                  color: 'white'
                }}>
                  <h2 style={{ margin: '0 0 8px 0', fontSize: '42px', fontWeight: 'bold' }}>
                    {generatedData.total_tasks_in_progress}
                  </h2>
                  <p style={{ margin: 0, fontSize: '16px', opacity: 0.9 }}>Tasks In Progress</p>
                </div>
              </div>

              {/* Team Progress Cards */}
              <div style={{ marginBottom: '25px' }}>
                <h4 style={{ color: '#333', marginBottom: '15px', fontSize: '20px' }}>👥 Team Progress</h4>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(3, 1fr)', 
                  gap: '16px' 
                }}>
                  {generatedData.team_progress && generatedData.team_progress.map((member) => (
                    <div 
                      key={member.user_id}
                      style={{ 
                        background: '#f9f9ff', 
                        padding: '20px', 
                        borderRadius: '12px',
                        border: '2px solid #e0e0ff',
                        textAlign: 'center'
                      }}
                    >
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #CE8FFB 0%, #7077FE 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '28px',
                        fontWeight: 'bold',
                        margin: '0 auto 12px'
                      }}>
                        {member.avatar}
                      </div>
                      <h5 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#333' }}>
                        {member.user_name}
                      </h5>
                      <div style={{ display: 'grid', gap: '8px' }}>
                        <div style={{ 
                          padding: '8px', 
                          background: 'white', 
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}>
                          <strong style={{ color: '#4CAF50' }}>{member.tasks_completed}</strong>
                          <span style={{ color: '#666', marginLeft: '4px' }}>completed</span>
                        </div>
                        <div style={{ 
                          padding: '8px', 
                          background: 'white', 
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}>
                          <strong style={{ color: '#FF9800' }}>{member.tasks_in_progress}</strong>
                          <span style={{ color: '#666', marginLeft: '4px' }}>in progress</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Custom Content */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '20px', color: '#7077FE' }}>Add Custom Message</h3>
            <textarea
              value={formData.custom_content}
              onChange={(e) => setFormData({ ...formData, custom_content: e.target.value })}
              placeholder="Add your custom message here... (Optional)"
              rows={6}
              style={{ 
                width: '100%', 
                padding: '12px', 
                border: '1px solid #ddd', 
                borderRadius: '8px',
                fontSize: '16px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
            <button 
              onClick={handlePreview}
              disabled={!generatedData}
              className="btn-outline"
              style={{ padding: '12px 24px' }}
            >
              <Eye size={18} /> Preview
            </button>
            <button 
              onClick={handleSendNewsletter}
              disabled={loading || !generatedData}
              className="gradient-btn"
              style={{ padding: '12px 24px' }}
            >
              <Send size={18} /> Send to All Team Members
            </button>
          </div>
        </div>
      ) : (
        /* Newsletter History */
        <div className="newsletter-history">
          {newsletters.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ color: '#666' }}>No newsletters sent yet</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '20px' }}>
              {newsletters.map((newsletter) => (
                <div key={newsletter.id} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ color: '#7077FE', marginBottom: '10px' }}>{newsletter.title}</h3>
                      <p style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>
                        <strong>Period:</strong> {newsletter.from_date} to {newsletter.to_date}
                      </p>
                      <p style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>
                        <strong>Sent:</strong> {new Date(newsletter.sent_at).toLocaleString()}
                      </p>
                      <p style={{ color: '#666', fontSize: '14px' }}>
                        <strong>Recipients:</strong> {newsletter.recipients_count} team members
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setPreviewData(newsletter);
                        setShowPreview(true);
                      }}
                      className="gradient-btn"
                      style={{ padding: '8px 16px' }}
                    >
                      <Eye size={16} /> View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <div style={{
              position: 'sticky',
              top: 0,
              background: 'white',
              padding: '20px',
              borderBottom: '1px solid #ddd',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, color: '#7077FE' }}>Newsletter Preview</h2>
              <button 
                onClick={() => setShowPreview(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{
                background: 'linear-gradient(135deg, #CE8FFB 0%, #7077FE 100%)',
                padding: '40px',
                borderRadius: '8px',
                color: 'white',
                textAlign: 'center',
                marginBottom: '20px'
              }}>
                <h1 style={{ margin: 0, fontSize: '28px' }}>📊 CNESS Hive Weekly Newsletter</h1>
                <p style={{ margin: '10px 0 0 0' }}>{previewData.title}</p>
                <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: 0.9 }}>
                  Period: {previewData.from_date} to {previewData.to_date}
                </p>
              </div>
              
              {previewData.custom_content && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ color: '#7077FE' }}>📝 Message from Leadership</h3>
                  <p style={{ lineHeight: '1.6' }}>{previewData.custom_content}</p>
                </div>
              )}
              
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#7077FE' }}>✅ Tasks Completed</h3>
                <div style={{ background: '#f9f9ff', padding: '15px', borderRadius: '8px' }}>
                  {Object.entries(previewData.generated_data?.tasks_completed || {}).map(([name, count]) => (
                    <div key={name} style={{ padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
                      <strong>{name}:</strong> {count} tasks
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsletterManagement;
